import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgTimezone, formatDateTimeInTz, dateTimeLocalToISO } from '@/utils/timezone';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  JobNameAutocomplete,
  JobTargetSelector,
  JobEquipmentPicker,
  JobUserAssigner,
  ActivityTypePicker,
} from '../../components/jobs';
import { ClipboardList, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import jobService from '../../services/jobs';
import { inductionRefusal } from '../../services/induction';
import InductionGapDialog from '../../components/induction/InductionGapDialog';
import type { InductionGap, InductionMode } from '../../types/induction';
import type { JobFormData, JobTargetInput } from '../../types/jobs';
import type { ActivityType } from '../../types/fieldWork';

// People before equipment: the picker puts gear already issued to the crew
// at the top, which it can only do once it knows who the crew is.
const STEPS = ['Details', 'Assignment', 'Equipment', 'Review'];

function JobCreate() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A 409 NOT_INDUCTED from the server: who is not inducted on what.
  const [inductionGaps, setInductionGaps] = useState<{ gaps: InductionGap[]; canOverride: boolean } | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [activityType, setActivityType] = useState<ActivityType | null>(null);
  // GPS follows the work type (mowing yes, pruning no) until somebody ticks it by hand.
  const [gpsTouched, setGpsTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [targets, setTargets] = useState<JobTargetInput[]>([]);
  const [targetFarmIds, setTargetFarmIds] = useState<number[]>([]);
  const [enableGps, setEnableGps] = useState(true);
  const [notes, setNotes] = useState('');

  // Equipment
  const [primaryEquipId, setPrimaryEquipId] = useState<number | null>(null);
  const [implementIds, setImplementIds] = useState<number[]>([]);
  const [workingWidth, setWorkingWidth] = useState<number | null>(null);

  // Assignment
  const [assignedUserIds, setAssignedUserIds] = useState<number[]>([]);
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');

  const canProceed = () => {
    if (step === 0) return name.trim().length > 0;
    if (step === 1) return true; // assignment optional at create time
    if (step === 2) return true; // equipment is optional
    return true;
  };

  const handleSubmit = async (inductionMode?: InductionMode, inductionNote?: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload: JobFormData = {
        name: name.trim(),
        scheduled_start: scheduledStart ? dateTimeLocalToISO(scheduledStart, orgTz) : undefined,
        scheduled_end: scheduledEnd ? dateTimeLocalToISO(scheduledEnd, orgTz) : undefined,
        enable_gps_tracking: enableGps,
      };
      if (activityType) payload.activity_type_id = activityType.id;
      if (description.trim()) payload.description = description.trim();
      if (targets.length > 0) payload.targets = targets;
      if (notes.trim()) payload.notes = notes.trim();
      if (assignedUserIds.length > 0) payload.assigned_user_ids = assignedUserIds;
      if (primaryEquipId) {
        payload.equipment = { primary_id: primaryEquipId, implement_ids: implementIds };
      }
      if (workingWidth) payload.working_width_m = workingWidth;
      if (inductionMode) {
        payload.induction_mode = inductionMode;
        if (inductionNote?.trim()) payload.induction_note = inductionNote.trim();
      }

      const res = await jobService.createJob(payload);

      if (res.success && res.data) {
        setInductionGaps(null);
        const held = (res.data as { induction_requests?: unknown[] }).induction_requests?.length ?? 0;
        navigate(`/jobs/${res.data.id}`, held ? { state: { inductionRequested: held } } : undefined);
      } else {
        setError((res as any)?.error?.message || (res as any)?.message || 'Job created but response was unexpected');
      }
    } catch (e: any) {
      const refusal = inductionRefusal(e);
      if (refusal && refusal.gaps.length) {
        // Not a failure to report — a question: sign them off, or ask a supervisor?
        setInductionGaps({ gaps: refusal.gaps, canOverride: refusal.canOverride });
        if (inductionMode) setError(refusal.message);
      } else {
        const msg = e?.message || e?.response?.data?.error?.message || e?.error?.message || 'Failed to create job';
        setError(msg);
        setInductionGaps(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="New Job"
        subtitle="Create a farm job or task"
        icon={<ClipboardList className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Step indicators */}
        {/* Four steps with connectors are ~570px — wider than any phone — so the
            row wraps and the connectors only show once there is room for them. */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer ${
                i === step
                  ? 'bg-indigo-100 text-indigo-700'
                  : i < step
                  ? 'bg-success-100 text-success-700'
                  : 'bg-neutral-100 text-neutral-400'
              }`}
                onClick={() => i < step && setStep(i)}
              >
                {i < step ? <Check className="h-4 w-4" /> : <span>{i + 1}</span>}
                {s}
              </div>
              {i < STEPS.length - 1 && <div className="hidden sm:block w-8 h-0.5 bg-neutral-200 mx-1" />}
            </div>
          ))}
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          {/* Step 0: Details */}
          {step === 0 && (
            <div className="space-y-4">
              <ActivityTypePicker
                value={activityType?.id ?? null}
                onChange={(_, type) => {
                  setActivityType(type);
                  if (type && !gpsTouched) setEnableGps(type.uses_gps_coverage);
                  if (type && !name.trim()) setName(type.name);
                }}
              />

              <JobNameAutocomplete value={name} onChange={setName} />

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Description (optional)</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  rows={2}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200"
                  placeholder="Details about the job..."
                />
              </div>

              <JobTargetSelector
                targets={targets}
                onChange={setTargets}
                onFarmIdsChange={setTargetFarmIds}
              />

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableGps}
                  onChange={e => { setEnableGps(e.target.checked); setGpsTouched(true); }}
                  className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200"
                />
                <span className="text-neutral-700">Enable GPS tracking</span>
              </label>
            </div>
          )}

          {/* Step 2: Equipment */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-500 mb-2">
                For equipment-based jobs (slashing, mowing, whipper snipping) choose the machine or
                tool, and any implement. For manual labour (picking, pruning), skip this step.
              </p>
              <JobEquipmentPicker
                farmIds={targetFarmIds}
                assigneeIds={assignedUserIds}
                primaryId={primaryEquipId}
                implementIds={implementIds}
                workingWidth={workingWidth}
                onPrimaryChange={setPrimaryEquipId}
                onImplementsChange={setImplementIds}
                onWorkingWidthChange={setWorkingWidth}
              />
            </div>
          )}

          {/* Step 1: Assignment */}
          {step === 1 && (
            <div className="space-y-4">
              <JobUserAssigner
                selectedIds={assignedUserIds}
                onChange={setAssignedUserIds}
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
                  <input
                    type="datetime-local"
                    value={scheduledStart}
                    onChange={e => setScheduledStart(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
                  <input
                    type="datetime-local"
                    value={scheduledEnd}
                    onChange={e => setScheduledEnd(e.target.value)}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200"
                  placeholder="Any additional instructions..."
                />
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-neutral-800">Review Job</h3>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-neutral-500">Name:</span> <span className="font-medium">{name}</span></div>
                {activityType && <div><span className="text-neutral-500">Type of work:</span> {activityType.name}</div>}
                {description && <div className="col-span-2"><span className="text-neutral-500">Description:</span> {description}</div>}
                {targets.length > 0 && <div><span className="text-neutral-500">Targets:</span> {targets.length} area(s) selected</div>}
                <div><span className="text-neutral-500">GPS Tracking:</span> {enableGps ? 'Yes' : 'No'}</div>
                {primaryEquipId && <div><span className="text-neutral-500">Equipment:</span> 1 machine{implementIds.length > 0 ? ` + ${implementIds.length} implement(s)` : ''}</div>}
                {workingWidth && <div><span className="text-neutral-500">Working Width:</span> {workingWidth}m</div>}
                {assignedUserIds.length > 0 && <div><span className="text-neutral-500">Assigned:</span> {assignedUserIds.length} user(s)</div>}
                {scheduledStart && <div><span className="text-neutral-500">Start:</span> {formatDateTimeInTz(dateTimeLocalToISO(scheduledStart, orgTz), orgTz)}</div>}
                {scheduledEnd && <div><span className="text-neutral-500">End:</span> {formatDateTimeInTz(dateTimeLocalToISO(scheduledEnd, orgTz), orgTz)}</div>}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
              )}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-100">
            <Button
              variant="outline"
              size="sm"
              onClick={() => step === 0 ? navigate('/jobs') : setStep(s => s - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>

            {step < STEPS.length - 1 ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSubmit()}
                disabled={submitting || !name.trim()}
              >
                {submitting ? 'Creating...' : 'Create Job'}
              </Button>
            )}
          </div>
        </div>
      </div>
      {inductionGaps && (
        <InductionGapDialog
          gaps={inductionGaps.gaps}
          canOverride={inductionGaps.canOverride}
          busy={submitting}
          error={error}
          onChoose={(mode, note) => handleSubmit(mode, note)}
          onClose={() => { setInductionGaps(null); setError(null); }}
        />
      )}
    </>
  );
}

export default JobCreate;
